import { useEffect, useMemo, useRef, useState } from 'react'
import { useI18n } from '../i18n/useI18n'
import { recordingCapability, selectRecordingMimeType } from '../lib/recordingMedia'
import { audioUploadError, normalizedAudioContentType } from '../lib/audioUpload'
import { audibleTrackKeys, clampTrackVolume, type RecordingTrackMix } from '../lib/recordingStudioMix'
import { estimateRecordingLatencyMs, normalizeLatencyMs } from '../lib/recordingStudioLatency'
import {
  countInBeatIntervalMs,
  DEFAULT_COUNT_IN_BPM,
  DEFAULT_COUNT_IN_BEATS,
  normalizeCountInBeats,
  normalizeCountInBpm,
  type RecordingStudioCountInBeats,
} from '../lib/recordingStudioCountIn'
import {
  DEFAULT_PARTIAL_RECORDING_ENABLED,
  DEFAULT_PRE_ROLL_SECONDS,
  DEFAULT_RECORDING_START_MS,
  formatTimelinePosition,
  normalizePreRollSeconds,
  normalizeTimelineStartMs,
  parseTimelinePosition,
  preRollWindow,
  type RecordingStudioPreRollSeconds,
} from '../lib/recordingStudioTimeline'
import { exportRecordingStudioMixToWav } from '../lib/recordingStudioExport'
import { clampPlayheadMs, naturalTakeEndMs, playheadFromElapsedMs } from '../lib/recordingStudioPlayhead'
import {
  compHasUsableSegments,
  compSegmentsValid,
  normalizeCompTimeMs,
  sortedCompSegments,
  type RecordingStudioCompSegment,
} from '../lib/recordingStudioComping'
import { decodeRecordingStudioProject, encodeRecordingStudioProject } from '../lib/recordingStudioProjectFile'
import {
  createRecordingStudioProject,
  deleteRecordingStudioProject,
  duplicateRecordingStudioProject,
  loadRecordingStudioProject,
  importRecordingStudioProject,
  loadRecordingStudioWorkspace,
  saveRecordingStudioProject,
  type RecordingStudioProjectSummary,
  type StoredRecordingStudioDraft,
} from '../lib/recordingStudioStorage'

type AccompanimentSource = 'NONE' | 'UPLOAD' | 'RECORD'

const UPLOADED_ACCOMPANIMENT_TRACK = 'accompaniment:upload'
interface DraftPart { id: number; name: string }
interface LocalTake {
  id: number
  number: number
  objectUrl: string
  mimeType: string
  recordedAt: string
  blob: Blob
  offsetMs: number
  timelineStartMs: number
}
interface RecordingStudioProps {
  ownerId: string
  onUnsavedChangesChange?: (dirty: boolean) => void
}

const DEFAULT_MIX: RecordingTrackMix = { volume: 1, muted: false, solo: false }

/**
 * 세뇨 녹음실 multitrack monitor 단계.
 *
 * - 새 녹음 파일은 MediaRecorder가 마이크 MediaStream만 입력으로 받습니다.
 * - 기존 active Take들은 별도의 HTMLAudioElement monitor mix로 재생합니다.
 * - 따라서 monitor mix가 디지털 경로로 새 Take에 합쳐지지 않습니다.
 * - 물리적 스피커 소리는 마이크에 다시 들어갈 수 있으므로 실제 오버더빙은 헤드폰 사용을 권장합니다.
 */
export function RecordingStudio({ ownerId, onUnsavedChangesChange }: RecordingStudioProps) {
  const { t } = useI18n()
  const [projectTitle, setProjectTitle] = useState('')
  const [accompanimentSource, setAccompanimentSource] = useState<AccompanimentSource>('NONE')
  const [uploadedAccompanimentName, setUploadedAccompanimentName] = useState('')
  const [accompanimentUploadBusy, setAccompanimentUploadBusy] = useState(false)
  const [parts, setParts] = useState<DraftPart[]>([])
  const [takesByTrack, setTakesByTrack] = useState<Record<string, LocalTake[]>>({})
  const [activeTakeByTrack, setActiveTakeByTrack] = useState<Record<string, number>>({})
  const [compEnabledByTrack, setCompEnabledByTrack] = useState<Record<string, boolean>>({})
  const [compSegmentsByTrack, setCompSegmentsByTrack] = useState<Record<string, RecordingStudioCompSegment[]>>({})
  const [trackMixByKey, setTrackMixByKey] = useState<Record<string, RecordingTrackMix>>({})
  const [recordingTrackKey, setRecordingTrackKey] = useState<string | null>(null)
  const [recordingNotice, setRecordingNotice] = useState('')
  const [hydrated, setHydrated] = useState(false)
  const [mixPlaying, setMixPlaying] = useState(false)
  const [playheadMs, setPlayheadMs] = useState(0)
  const [projectDurationMs, setProjectDurationMs] = useState(0)
  const [latencyMode, setLatencyMode] = useState<'AUTO' | 'MANUAL'>('AUTO')
  const [manualLatencyMs, setManualLatencyMs] = useState(0)
  const [latencyEstimateMs, setLatencyEstimateMs] = useState(0)
  const [countInBeats, setCountInBeats] = useState<RecordingStudioCountInBeats>(DEFAULT_COUNT_IN_BEATS)
  const [countInBpm, setCountInBpm] = useState(DEFAULT_COUNT_IN_BPM)
  const [countInRemaining, setCountInRemaining] = useState(0)
  const [partialRecordingEnabled, setPartialRecordingEnabled] = useState(DEFAULT_PARTIAL_RECORDING_ENABLED)
  const [recordingStartMs, setRecordingStartMs] = useState(DEFAULT_RECORDING_START_MS)
  const [recordingStartText, setRecordingStartText] = useState(formatTimelinePosition(DEFAULT_RECORDING_START_MS))
  const [preRollSeconds, setPreRollSeconds] = useState<RecordingStudioPreRollSeconds>(DEFAULT_PRE_ROLL_SECONDS)
  const [preRollActive, setPreRollActive] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [projects, setProjects] = useState<RecordingStudioProjectSummary[]>([])
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null)
  const [projectBusy, setProjectBusy] = useState(false)

  const nextPartId = useRef(1)
  const nextTakeId = useRef(1)
  const nextCompSegmentId = useRef(1)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<BlobPart[]>([])
  const objectUrlsRef = useRef(new Set<string>())
  const monitorAudioRef = useRef<Record<string, HTMLAudioElement>>({})
  const monitorTimerRef = useRef<number[]>([])
  const playheadMsRef = useRef(0)
  const projectDurationMsRef = useRef(0)
  const masterPlaybackActiveRef = useRef(false)
  const masterPlaybackStartPositionRef = useRef(0)
  const masterPlaybackStartedAtRef = useRef<number | null>(null)
  const masterPlayheadTimerRef = useRef<number | null>(null)
  const liveResyncTimerRef = useRef<number | null>(null)
  const audioDurationCacheRef = useRef<Map<string, number>>(new Map())
  const preRollAbortRef = useRef(0)
  const countInAbortRef = useRef(0)
  const countInAudioContextRef = useRef<AudioContext | null>(null)

  const accompanimentUploadInputRef = useRef<HTMLInputElement | null>(null)
  const projectImportInputRef = useRef<HTMLInputElement | null>(null)
  function debug(message: string, detail?: unknown) {
    if (import.meta.env.DEV) console.debug(`[mioSegno:recording-studio] ${message}`, detail ?? '')
  }

  const allTrackKeys = useMemo(() => {
    const keys: string[] = []
    if (accompanimentSource === 'RECORD') keys.push('accompaniment')
    if (accompanimentSource === 'UPLOAD') keys.push(UPLOADED_ACCOMPANIMENT_TRACK)
    for (const part of parts) keys.push(`part:${part.id}`)
    return keys
  }, [accompanimentSource, parts])

  function getMix(trackKey: string): RecordingTrackMix {
    return trackMixByKey[trackKey] ?? DEFAULT_MIX
  }

  function activeTake(trackKey: string): LocalTake | null {
    const activeId = activeTakeByTrack[trackKey]
    if (!activeId) return null
    return (takesByTrack[trackKey] ?? []).find((take) => take.id === activeId) ?? null
  }

  function trackHasUsableComp(trackKey: string): boolean {
    const takeIds = new Set((takesByTrack[trackKey] ?? []).map((take) => take.id))
    return compHasUsableSegments(compEnabledByTrack[trackKey], compSegmentsByTrack[trackKey], takeIds)
  }

  function trackHasPlayback(trackKey: string): boolean {
    return trackHasUsableComp(trackKey) || Boolean(activeTake(trackKey))
  }

  function validateTrackComp(trackKey: string): boolean {
    if (!compEnabledByTrack[trackKey]) return true
    const segments = compSegmentsByTrack[trackKey] ?? []
    if (segments.length === 0) return true
    const takeIds = new Set((takesByTrack[trackKey] ?? []).map((take) => take.id))
    return compSegmentsValid(segments, takeIds)
  }

  function revokeAllObjectUrls() {
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
    objectUrlsRef.current.clear()
  }

  function commitPlayhead(positionMs: number) {
    const next = clampPlayheadMs(positionMs, projectDurationMsRef.current)
    playheadMsRef.current = next
    setPlayheadMs(next)
  }

  function currentMasterPlayheadMs(): number {
    if (!masterPlaybackActiveRef.current || masterPlaybackStartedAtRef.current === null) {
      return playheadMsRef.current
    }
    return playheadFromElapsedMs(
      masterPlaybackStartPositionRef.current,
      performance.now() - masterPlaybackStartedAtRef.current,
      projectDurationMsRef.current,
    )
  }

  function stopMasterPlayheadClock() {
    if (masterPlayheadTimerRef.current !== null) {
      window.clearInterval(masterPlayheadTimerRef.current)
      masterPlayheadTimerRef.current = null
    }
  }

  function clearMonitorAudioOnly() {
    monitorTimerRef.current.forEach((timer) => window.clearTimeout(timer))
    monitorTimerRef.current = []
    for (const audio of Object.values(monitorAudioRef.current)) {
      audio.pause()
    }
    monitorAudioRef.current = {}
  }

  function finishMasterPlaybackAt(positionMs: number) {
    stopMasterPlayheadClock()
    clearMonitorAudioOnly()
    masterPlaybackActiveRef.current = false
    masterPlaybackStartedAtRef.current = null
    commitPlayhead(positionMs)
    setMixPlaying(false)
  }

  function startMasterPlayheadClock(startMs: number) {
    stopMasterPlayheadClock()
    masterPlaybackActiveRef.current = true
    masterPlaybackStartPositionRef.current = startMs
    masterPlaybackStartedAtRef.current = performance.now()
    commitPlayhead(startMs)

    masterPlayheadTimerRef.current = window.setInterval(() => {
      const next = currentMasterPlayheadMs()
      commitPlayhead(next)
      const duration = projectDurationMsRef.current
      if (duration > 0 && next >= duration) {
        finishMasterPlaybackAt(duration)
      }
    }, 100)
  }

  function stopMonitorMix() {
    const current = currentMasterPlayheadMs()
    stopMasterPlayheadClock()
    clearMonitorAudioOnly()
    if (masterPlaybackActiveRef.current) commitPlayhead(current)
    masterPlaybackActiveRef.current = false
    masterPlaybackStartedAtRef.current = null
    setMixPlaying(false)
  }

  async function startMonitorMix(excludeTrackKey?: string, timelinePositionMs = playheadMsRef.current): Promise<boolean> {
    const isMasterPlayback = excludeTrackKey === undefined
    const projectPositionMs = clampPlayheadMs(timelinePositionMs, projectDurationMsRef.current)

    stopMasterPlayheadClock()
    clearMonitorAudioOnly()
    masterPlaybackActiveRef.current = false
    masterPlaybackStartedAtRef.current = null

    const availableKeys = allTrackKeys.filter((key) => key !== excludeTrackKey && trackHasPlayback(key))
    const audibleKeys = audibleTrackKeys(availableKeys, trackMixByKey)
    if (audibleKeys.length === 0) {
      if (isMasterPlayback) commitPlayhead(projectPositionMs)
      setMixPlaying(false)
      return false
    }

    if (audibleKeys.some((key) => !validateTrackComp(key))) {
      setRecordingNotice(t('recording.comp.invalid'))
      if (isMasterPlayback) commitPlayhead(projectPositionMs)
      return false
    }

    const audios: Record<string, HTMLAudioElement> = {}
    const startPlans: Array<{
      id: string
      trackKey: string
      audio: HTMLAudioElement
      startMs: number
      endMs: number
      sourceMs: number
    }> = []

    for (const key of audibleKeys) {
      const mix = getMix(key)
      const takes = takesByTrack[key] ?? []
      if (trackHasUsableComp(key)) {
        for (const segment of sortedCompSegments(compSegmentsByTrack[key])) {
          if (segment.endMs <= projectPositionMs) continue
          const take = takes.find((item) => item.id === segment.takeId)
          if (!take) continue
          const takeTimelineStartMs = normalizeTimelineStartMs(take.timelineStartMs ?? 0)
          const effectiveStartMs = Math.max(segment.startMs, takeTimelineStartMs)
          if (segment.endMs <= effectiveStartMs) continue
          const id = `${key}:comp:${segment.id}`
          const audio = new Audio(take.objectUrl)
          audio.preload = 'auto'
          audio.volume = clampTrackVolume(mix.volume)
          audios[id] = audio
          const startMs = Math.max(projectPositionMs, effectiveStartMs)
          const sourceMs = normalizeLatencyMs(take.offsetMs ?? 0) + startMs - takeTimelineStartMs
          startPlans.push({ id, trackKey: key, audio, startMs, endMs: segment.endMs, sourceMs })
        }
      } else {
        const take = activeTake(key)
        if (!take) continue
        const takeStartMs = normalizeTimelineStartMs(take.timelineStartMs ?? 0)
        const id = `${key}:active`
        const audio = new Audio(take.objectUrl)
        audio.preload = 'auto'
        audio.volume = clampTrackVolume(mix.volume)
        audios[id] = audio
        const startMs = Math.max(projectPositionMs, takeStartMs)
        const sourceMs = normalizeLatencyMs(take.offsetMs ?? 0) + startMs - takeStartMs
        startPlans.push({ id, trackKey: key, audio, startMs, endMs: Number.POSITIVE_INFINITY, sourceMs })
      }
    }

    monitorAudioRef.current = audios

    try {
      const immediate: Promise<void>[] = []
      for (const plan of startPlans) {
        plan.audio.currentTime = Math.max(0, plan.sourceMs) / 1000
        const delayMs = Math.max(0, plan.startMs - projectPositionMs)
        const durationMs = Number.isFinite(plan.endMs) ? Math.max(0, plan.endMs - plan.startMs) : Number.POSITIVE_INFINITY

        const begin = () => {
          void plan.audio.play().catch((error) => debug('monitor playback failed', { id: plan.id, trackKey: plan.trackKey, error }))
          if (Number.isFinite(durationMs)) {
            const stopTimer = window.setTimeout(() => plan.audio.pause(), durationMs)
            monitorTimerRef.current.push(stopTimer)
          }
        }

        if (delayMs <= 0) {
          immediate.push(plan.audio.play())
          if (Number.isFinite(durationMs)) {
            const stopTimer = window.setTimeout(() => plan.audio.pause(), durationMs)
            monitorTimerRef.current.push(stopTimer)
          }
        } else {
          const timer = window.setTimeout(begin, delayMs)
          monitorTimerRef.current.push(timer)
        }
      }

      await Promise.all(immediate)
      if (isMasterPlayback) startMasterPlayheadClock(projectPositionMs)
      setMixPlaying(true)
      debug('monitor mix started', { excludeTrackKey, audibleKeys, timelinePositionMs: projectPositionMs, isMasterPlayback })
      return true
    } catch (error) {
      stopMasterPlayheadClock()
      clearMonitorAudioOnly()
      masterPlaybackActiveRef.current = false
      masterPlaybackStartedAtRef.current = null
      if (isMasterPlayback) commitPlayhead(projectPositionMs)
      setMixPlaying(false)
      setRecordingNotice(t('recording.mix.playbackError'))
      debug('monitor mix playback failed', error)
      return false
    }
  }

  async function readTakeDurationMs(take: LocalTake): Promise<number> {
    const cached = audioDurationCacheRef.current.get(take.objectUrl)
    if (cached !== undefined) return cached

    const duration = await new Promise<number>((resolve) => {
      const audio = new Audio()
      const finish = (value: number) => {
        audio.removeAttribute('src')
        audio.load()
        resolve(value)
      }
      audio.preload = 'metadata'
      audio.addEventListener('loadedmetadata', () => {
        const value = Number.isFinite(audio.duration) ? Math.max(0, audio.duration * 1000) : 0
        finish(value)
      }, { once: true })
      audio.addEventListener('error', () => finish(0), { once: true })
      audio.src = take.objectUrl
    })

    audioDurationCacheRef.current.set(take.objectUrl, duration)
    return duration
  }

  function seekMasterPlayhead(positionMs: number) {
    const target = clampPlayheadMs(positionMs, projectDurationMsRef.current)
    const wasPlaying = masterPlaybackActiveRef.current
    if (wasPlaying) {
      void startMonitorMix(undefined, target)
    } else {
      commitPlayhead(target)
    }
  }

  function goMasterPlayheadToStart() {
    if (masterPlaybackActiveRef.current) {
      void startMonitorMix(undefined, 0)
    } else {
      commitPlayhead(0)
    }
  }


  function buildStoredDraft(): StoredRecordingStudioDraft {
    const persistedTakes = Object.fromEntries(Object.entries(takesByTrack).map(([trackKey, takes]) => [trackKey, takes.map(({ objectUrl: _objectUrl, ...take }) => take)]))
    return { projectTitle, accompanimentSource, uploadedAccompanimentName, parts, takesByTrack: persistedTakes, activeTakeByTrack, compEnabledByTrack, compSegmentsByTrack, trackMixByKey, latencyMode, manualLatencyMs, latencyEstimateMs, countInBeats, countInBpm, partialRecordingEnabled, recordingStartMs, preRollSeconds, updatedAt: new Date().toISOString() }
  }

  function applyStoredDraft(draft: StoredRecordingStudioDraft) {
    revokeAllObjectUrls()
    const rebuilt: Record<string, LocalTake[]> = {}
    let maxTakeId = 0
    for (const [trackKey, takes] of Object.entries(draft.takesByTrack ?? {})) {
      rebuilt[trackKey] = takes.map((take) => {
        const objectUrl = URL.createObjectURL(take.blob)
        objectUrlsRef.current.add(objectUrl)
        maxTakeId = Math.max(maxTakeId, take.id)
        return { ...take, offsetMs: normalizeLatencyMs(take.offsetMs ?? 0), timelineStartMs: normalizeTimelineStartMs(take.timelineStartMs ?? 0), objectUrl }
      })
    }
    const maxPartId = Math.max(0, ...(draft.parts ?? []).map((part) => part.id))
    setProjectTitle(draft.projectTitle ?? '')
    setAccompanimentSource(draft.accompanimentSource ?? 'NONE')
    setUploadedAccompanimentName(draft.uploadedAccompanimentName ?? '')
    setParts(draft.parts ?? [])
    setTakesByTrack(rebuilt)
    setActiveTakeByTrack(draft.activeTakeByTrack ?? {})
    setCompEnabledByTrack(draft.compEnabledByTrack ?? {})
    setCompSegmentsByTrack(draft.compSegmentsByTrack ?? {})
    setTrackMixByKey(draft.trackMixByKey ?? {})
    setLatencyMode(draft.latencyMode ?? 'AUTO')
    setManualLatencyMs(normalizeLatencyMs(draft.manualLatencyMs ?? 0))
    setLatencyEstimateMs(normalizeLatencyMs(draft.latencyEstimateMs ?? 0))
    setCountInBeats(normalizeCountInBeats(draft.countInBeats ?? DEFAULT_COUNT_IN_BEATS))
    setCountInBpm(normalizeCountInBpm(draft.countInBpm ?? DEFAULT_COUNT_IN_BPM))
    setPartialRecordingEnabled(draft.partialRecordingEnabled ?? DEFAULT_PARTIAL_RECORDING_ENABLED)
    const restoredStartMs = normalizeTimelineStartMs(draft.recordingStartMs ?? DEFAULT_RECORDING_START_MS)
    setRecordingStartMs(restoredStartMs)
    setRecordingStartText(formatTimelinePosition(restoredStartMs))
    setPreRollSeconds(normalizePreRollSeconds(draft.preRollSeconds ?? DEFAULT_PRE_ROLL_SECONDS))
    projectDurationMsRef.current = 0
    setProjectDurationMs(0)
    commitPlayhead(0)
    nextPartId.current = maxPartId + 1
    nextTakeId.current = maxTakeId + 1
    const maxCompSegmentId = Math.max(0, ...Object.values(draft.compSegmentsByTrack ?? {}).flat().map((segment) => segment.id))
    nextCompSegmentId.current = maxCompSegmentId + 1
  }

  async function saveCurrentProjectNow() {
    if (!currentProjectId || !hydrated) return
    setProjects(await saveRecordingStudioProject(ownerId, currentProjectId, buildStoredDraft()))
  }

  async function openProject(projectId: string) {
    if (!currentProjectId || projectId === currentProjectId || recordingTrackKey || exporting || projectBusy) return
    setProjectBusy(true)
    stopMonitorMix()
    try {
      await saveCurrentProjectNow()
      const loaded = await loadRecordingStudioProject(ownerId, projectId)
      applyStoredDraft(loaded.draft)
      setProjects(loaded.projects)
      setCurrentProjectId(projectId)
      setRecordingNotice(t('recording.projects.opened'))
      onUnsavedChangesChange?.(false)
    } catch (error) {
      setRecordingNotice(t('recording.projects.error'))
      debug('project open failed', error)
    } finally {
      setProjectBusy(false)
    }
  }

  async function newProject() {
    if (recordingTrackKey || exporting || projectBusy) return
    setProjectBusy(true)
    stopMonitorMix()
    try {
      await saveCurrentProjectNow()
      const created = await createRecordingStudioProject(ownerId)
      applyStoredDraft(created.draft)
      setProjects(created.projects)
      setCurrentProjectId(created.currentProjectId)
      setRecordingNotice(t('recording.projects.created'))
      onUnsavedChangesChange?.(false)
    } catch (error) {
      setRecordingNotice(t('recording.projects.error'))
      debug('project create failed', error)
    } finally {
      setProjectBusy(false)
    }
  }

  async function duplicateProject() {
    if (!currentProjectId || recordingTrackKey || exporting || projectBusy) return
    setProjectBusy(true)
    stopMonitorMix()
    try {
      await saveCurrentProjectNow()
      const sourceTitle = projectTitle.trim() || t('recording.projects.untitled')
      const duplicated = await duplicateRecordingStudioProject(ownerId, currentProjectId, t('recording.projects.copyName', { title: sourceTitle }))
      applyStoredDraft(duplicated.draft)
      setProjects(duplicated.projects)
      setCurrentProjectId(duplicated.currentProjectId)
      setRecordingNotice(t('recording.projects.duplicated'))
      onUnsavedChangesChange?.(false)
    } catch (error) {
      setRecordingNotice(t('recording.projects.error'))
      debug('project duplicate failed', error)
    } finally {
      setProjectBusy(false)
    }
  }

  async function exportCurrentProjectFile() {
    if (!currentProjectId || projectBusy || recordingTrackKey || exporting) return
    setProjectBusy(true)
    try {
      const draft = buildStoredDraft()
      const nextProjects = await saveRecordingStudioProject(ownerId, currentProjectId, draft)
      setProjects(nextProjects)

      const file = await encodeRecordingStudioProject(draft)
      const url = URL.createObjectURL(file)
      const link = document.createElement('a')
      link.href = url
      link.download = `${safeExportName(projectTitle, 'mioSegno-project')}.miosegno-studio`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 1000)

      setRecordingNotice(t('recording.projects.exportReady'))
      debug('project file exported', { currentProjectId, size: file.size })
    } catch (error) {
      setRecordingNotice(t('recording.projects.fileError'))
      debug('project file export failed', error)
    } finally {
      setProjectBusy(false)
    }
  }

  async function importProjectFile(file: File) {
    if (projectBusy || recordingTrackKey || exporting) return
    setProjectBusy(true)
    stopMonitorMix()
    try {
      await saveCurrentProjectNow()
      const draft = await decodeRecordingStudioProject(file)
      const imported = await importRecordingStudioProject(ownerId, draft)

      applyStoredDraft(imported.draft)
      setProjects(imported.projects)
      setCurrentProjectId(imported.currentProjectId)
      setRecordingNotice(t('recording.projects.imported'))
      onUnsavedChangesChange?.(false)

      debug('project file imported', {
        fileName: file.name,
        projectId: imported.currentProjectId,
        size: file.size,
      })
    } catch (error) {
      setRecordingNotice(t('recording.projects.fileError'))
      debug('project file import failed', error)
    } finally {
      if (projectImportInputRef.current) projectImportInputRef.current.value = ''
      setProjectBusy(false)
    }
  }

  async function deleteCurrentProject() {
    if (!currentProjectId || recordingTrackKey || exporting || projectBusy) return
    if (!window.confirm(t('recording.projects.deleteConfirm'))) return
    setProjectBusy(true)
    stopMonitorMix()
    try {
      const deleted = await deleteRecordingStudioProject(ownerId, currentProjectId)
      applyStoredDraft(deleted.draft)
      setProjects(deleted.projects)
      setCurrentProjectId(deleted.currentProjectId)
      setRecordingNotice(t('recording.projects.deleted'))
      onUnsavedChangesChange?.(false)
    } catch (error) {
      setRecordingNotice(t('recording.projects.error'))
      debug('project delete failed', error)
    } finally {
      setProjectBusy(false)
    }
  }

  useEffect(() => () => {
    countInAbortRef.current += 1
    const countInContext = countInAudioContextRef.current
    countInAudioContextRef.current = null
    if (countInContext && countInContext.state !== 'closed') void countInContext.close().catch(() => undefined)
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
    streamRef.current?.getTracks().forEach((track) => track.stop())
    stopMonitorMix()
    revokeAllObjectUrls()
  }, [])

  useEffect(() => {
    let cancelled = false
    setHydrated(false)
    setProjectBusy(true)
    void loadRecordingStudioWorkspace(ownerId)
      .then((workspace) => {
        if (cancelled) return
        applyStoredDraft(workspace.draft)
        setProjects(workspace.projects)
        setCurrentProjectId(workspace.currentProjectId)
        setHydrated(true)
        setProjectBusy(false)
        setRecordingNotice(workspace.migratedLegacyDraft ? t('recording.projects.migrated') : t('recording.persistence.restored'))
        onUnsavedChangesChange?.(false)
      })
      .catch((error) => {
        if (cancelled) return
        setHydrated(true)
        setProjectBusy(false)
        setRecordingNotice(t('recording.persistence.error'))
        debug('workspace restore failed', error)
      })
    return () => { cancelled = true }
  }, [ownerId])

  useEffect(() => {
    if (!hydrated || !currentProjectId || projectBusy || recordingTrackKey) return
    const timer = window.setTimeout(() => {
      const persistedTakes = Object.fromEntries(
        Object.entries(takesByTrack).map(([trackKey, takes]) => [
          trackKey,
          takes.map(({ objectUrl: _objectUrl, ...take }) => take),
        ]),
      )

      const draft: StoredRecordingStudioDraft = {
        projectTitle,
        accompanimentSource,
        uploadedAccompanimentName,
        parts,
        takesByTrack: persistedTakes,
        activeTakeByTrack,
        compEnabledByTrack,
        compSegmentsByTrack,
        trackMixByKey,
        latencyMode,
        manualLatencyMs,
        latencyEstimateMs,
        countInBeats,
        countInBpm,
        partialRecordingEnabled,
        recordingStartMs,
        preRollSeconds,
        updatedAt: new Date().toISOString(),
      }

      if (!currentProjectId) return
      void saveRecordingStudioProject(ownerId, currentProjectId, draft)
        .then((nextProjects) => {
          setProjects(nextProjects)
          onUnsavedChangesChange?.(false)
          debug('local draft saved', { ownerId })
        })
        .catch((error) => {
          onUnsavedChangesChange?.(true)
          setRecordingNotice(t('recording.persistence.error'))
          debug('local draft save failed', error)
        })
    }, 350)

    return () => window.clearTimeout(timer)
  }, [ownerId, currentProjectId, hydrated, projectBusy, recordingTrackKey, projectTitle, accompanimentSource, parts, takesByTrack, activeTakeByTrack, compEnabledByTrack, compSegmentsByTrack, trackMixByKey, latencyMode, manualLatencyMs, latencyEstimateMs, countInBeats, countInBpm, partialRecordingEnabled, recordingStartMs, preRollSeconds])

  useEffect(() => {
    let cancelled = false

    async function refreshProjectDuration() {
      let durationMs = 0

      for (const key of allTrackKeys) {
        if (!trackHasPlayback(key)) continue

        if (trackHasUsableComp(key)) {
          for (const segment of sortedCompSegments(compSegmentsByTrack[key])) {
            durationMs = Math.max(durationMs, segment.endMs)
          }
          continue
        }

        const take = activeTake(key)
        if (!take) continue
        const mediaDurationMs = await readTakeDurationMs(take)
        if (cancelled) return
        durationMs = Math.max(
          durationMs,
          naturalTakeEndMs(
            normalizeTimelineStartMs(take.timelineStartMs ?? 0),
            mediaDurationMs,
            normalizeLatencyMs(take.offsetMs ?? 0),
          ),
        )
      }

      if (cancelled) return
      const normalized = Math.max(0, Math.round(durationMs))
      projectDurationMsRef.current = normalized
      setProjectDurationMs(normalized)
      if (playheadMsRef.current > normalized && normalized > 0) commitPlayhead(normalized)
    }

    void refreshProjectDuration()
    return () => { cancelled = true }
  }, [allTrackKeys, takesByTrack, activeTakeByTrack, compEnabledByTrack, compSegmentsByTrack])

  useEffect(() => {
    if (!masterPlaybackActiveRef.current) return
    const position = currentMasterPlayheadMs()
    if (liveResyncTimerRef.current !== null) window.clearTimeout(liveResyncTimerRef.current)
    liveResyncTimerRef.current = window.setTimeout(() => {
      liveResyncTimerRef.current = null
      void startMonitorMix(undefined, position)
    }, 20)
  }, [trackMixByKey, takesByTrack])

  useEffect(() => () => {
    if (liveResyncTimerRef.current !== null) window.clearTimeout(liveResyncTimerRef.current)
    stopMasterPlayheadClock()
  }, [])

  // recording notice auto-clear: transient status banners should not occupy the workspace indefinitely.
  useEffect(() => {
    if (!recordingNotice) return
    const timer = window.setTimeout(() => setRecordingNotice(''), 5000)
    return () => window.clearTimeout(timer)
  }, [recordingNotice])

  function markDirty() { onUnsavedChangesChange?.(true) }

  async function renameCurrentProject() {
    if (!currentProjectId || projectBusy || recordingTrackKey || exporting) return

    const currentName = projectTitle.trim() || t('recording.projects.untitled')
    const entered = window.prompt(t('recording.projects.renamePrompt'), currentName)
    if (entered === null) return

    const nextName = entered.trim()
    if (!nextName) {
      setRecordingNotice(t('recording.projects.nameRequired'))
      return
    }
    if (nextName === projectTitle.trim()) return

    setProjectBusy(true)
    try {
      const draft: StoredRecordingStudioDraft = {
        ...buildStoredDraft(),
        projectTitle: nextName,
        updatedAt: new Date().toISOString(),
      }
      const nextProjects = await saveRecordingStudioProject(ownerId, currentProjectId, draft)
      setProjectTitle(nextName)
      setProjects(nextProjects)
      setRecordingNotice(t('recording.projects.renamed'))
      onUnsavedChangesChange?.(false)
      debug('project renamed', { projectId: currentProjectId, title: nextName })
    } catch (error) {
      setRecordingNotice(t('recording.projects.error'))
      debug('project rename failed', error)
    } finally {
      setProjectBusy(false)
    }
  }

  async function uploadedAudioIsDecodable(file: Blob): Promise<boolean> {
    const context = new AudioContext()
    try {
      const bytes = await file.arrayBuffer()
      await context.decodeAudioData(bytes.slice(0))
      return true
    } catch (error) {
      debug('uploaded accompaniment decode failed', error)
      return false
    } finally {
      await context.close()
    }
  }

  async function chooseUploadedAccompaniment(file: File) {
    if (accompanimentUploadBusy || recordingTrackKey !== null || exporting || projectBusy) return
    setAccompanimentUploadBusy(true)
    try {
      const policyError = audioUploadError(file)
      if (policyError) {
        setRecordingNotice(t('recording.accompaniment.uploadInvalid'))
        debug('uploaded accompaniment rejected by policy', { name: file.name, type: file.type, size: file.size, policyError })
        return
      }

      if (!(await uploadedAudioIsDecodable(file))) {
        setRecordingNotice(t('recording.accompaniment.uploadDecodeError'))
        return
      }

      stopMonitorMix()

      const previous = activeTake(UPLOADED_ACCOMPANIMENT_TRACK)
      if (previous) {
        URL.revokeObjectURL(previous.objectUrl)
        objectUrlsRef.current.delete(previous.objectUrl)
      }

      const objectUrl = URL.createObjectURL(file)
      objectUrlsRef.current.add(objectUrl)
      const takeId = nextTakeId.current++
      const uploadedTake: LocalTake = {
        id: takeId,
        number: 1,
        mimeType: normalizedAudioContentType(file),
        recordedAt: new Date().toISOString(),
        blob: file,
        offsetMs: 0,
        timelineStartMs: 0,
        objectUrl,
      }

      setTakesByTrack((current) => ({ ...current, [UPLOADED_ACCOMPANIMENT_TRACK]: [uploadedTake] }))
      setActiveTakeByTrack((current) => ({ ...current, [UPLOADED_ACCOMPANIMENT_TRACK]: takeId }))
      setUploadedAccompanimentName(file.name)
      setAccompanimentSource('UPLOAD')
      setRecordingNotice(t('recording.accompaniment.uploadReady'))
      markDirty()
      debug('uploaded accompaniment ready', { name: file.name, type: uploadedTake.mimeType, size: file.size })
    } finally {
      setAccompanimentUploadBusy(false)
      if (accompanimentUploadInputRef.current) accompanimentUploadInputRef.current.value = ''
    }
  }

  function changeAccompanimentSource(source: AccompanimentSource) {
    if (recordingTrackKey === 'accompaniment') return
    stopMonitorMix()
    setAccompanimentSource(source)
    setRecordingNotice('')
    markDirty()
  }

  function addPart() {
    const id = nextPartId.current++
    setParts((current) => [...current, { id, name: '' }])
    markDirty()
  }

  function renamePart(id: number, name: string) {
    setParts((current) => current.map((part) => part.id === id ? { ...part, name } : part))
    markDirty()
  }

  function deletePart(id: number) {
    if (!window.confirm(t('recording.part.deleteConfirm'))) return
    const key = `part:${id}`
    if (recordingTrackKey === key) return
    stopMonitorMix()
    for (const take of takesByTrack[key] ?? []) {
      URL.revokeObjectURL(take.objectUrl)
      objectUrlsRef.current.delete(take.objectUrl)
    }
    setParts((current) => current.filter((part) => part.id !== id))
    setTakesByTrack((current) => { const next = { ...current }; delete next[key]; return next })
    setActiveTakeByTrack((current) => { const next = { ...current }; delete next[key]; return next })
    setTrackMixByKey((current) => { const next = { ...current }; delete next[key]; return next })
    markDirty()
  }

  function updateTrackMix(trackKey: string, patch: Partial<RecordingTrackMix>) {
    setTrackMixByKey((current) => ({
      ...current,
      [trackKey]: { ...(current[trackKey] ?? DEFAULT_MIX), ...patch },
    }))
    markDirty()
  }

  function updateTakeOffset(trackKey: string, takeId: number, offsetMs: number) {
    setTakesByTrack((current) => ({
      ...current,
      [trackKey]: (current[trackKey] ?? []).map((take) =>
        take.id === takeId ? { ...take, offsetMs: normalizeLatencyMs(offsetMs) } : take,
      ),
    }))
    markDirty()
  }

  function commitRecordingStartText(): number | null {
    const parsed = parseTimelinePosition(recordingStartText)
    if (parsed === null) {
      setRecordingNotice(t('recording.partial.invalidPosition'))
      return null
    }
    setRecordingStartMs(parsed)
    setRecordingStartText(formatTimelinePosition(parsed))
    return parsed
  }

  async function waitForPreRoll(durationMs: number): Promise<boolean> {
    if (durationMs <= 0) return true
    const token = ++preRollAbortRef.current
    setPreRollActive(true)
    try {
      await new Promise<void>((resolve) => window.setTimeout(resolve, durationMs))
      return token === preRollAbortRef.current
    } finally {
      if (token === preRollAbortRef.current) setPreRollActive(false)
    }
  }

  function cancelPreRoll() {
    preRollAbortRef.current += 1
    setPreRollActive(false)
    stopMonitorMix()
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    recorderRef.current = null
    chunksRef.current = []
    setRecordingTrackKey(null)
    setRecordingNotice(t('recording.partial.cancelled'))
    debug('pre-roll cancelled')
  }

  async function playCountInClick(context: AudioContext, accent: boolean) {
    const oscillator = context.createOscillator()
    const gain = context.createGain()
    oscillator.type = 'sine'
    oscillator.frequency.value = accent ? 1200 : 880
    const now = context.currentTime
    gain.gain.setValueAtTime(0.0001, now)
    gain.gain.exponentialRampToValueAtTime(accent ? 0.22 : 0.14, now + 0.004)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.07)
    oscillator.connect(gain)
    gain.connect(context.destination)
    oscillator.start(now)
    oscillator.stop(now + 0.08)
  }

  async function runCountIn(): Promise<boolean> {
    const beats = normalizeCountInBeats(countInBeats)
    if (beats === 0) return true

    const token = ++countInAbortRef.current
    const intervalMs = countInBeatIntervalMs(countInBpm)
    const AudioContextCtor = window.AudioContext
    let context: AudioContext | null = null

    try {
      if (AudioContextCtor) {
        context = new AudioContextCtor()
        countInAudioContextRef.current = context
        if (context.state === 'suspended') await context.resume()
      }

      for (let index = 0; index < beats; index += 1) {
        if (token !== countInAbortRef.current) return false
        const remaining = beats - index
        setCountInRemaining(remaining)
        if (context) await playCountInClick(context, index === 0)
        await new Promise<void>((resolve) => {
          window.setTimeout(resolve, intervalMs)
        })
      }

      if (token !== countInAbortRef.current) return false
      setCountInRemaining(0)
      return true
    } finally {
      if (countInAudioContextRef.current === context) countInAudioContextRef.current = null
      if (context && context.state !== 'closed') {
        try { await context.close() } catch { /* browser audio cleanup only */ }
      }
    }
  }

  function cancelCountIn() {
    countInAbortRef.current += 1
    setCountInRemaining(0)
    const context = countInAudioContextRef.current
    countInAudioContextRef.current = null
    if (context && context.state !== 'closed') void context.close().catch(() => undefined)

    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    recorderRef.current = null
    chunksRef.current = []
    setRecordingTrackKey(null)
    setRecordingNotice(t('recording.countIn.cancelled'))
    debug('count-in cancelled')
  }

  async function estimateCurrentLatency(stream: MediaStream): Promise<number> {
    const inputSettings = stream.getAudioTracks()[0]?.getSettings() as MediaTrackSettings & { latency?: number } | undefined
    const inputLatencySeconds = inputSettings?.latency ?? 0
    let baseLatencySeconds = 0
    let outputLatencySeconds = 0

    const AudioContextCtor = window.AudioContext
    if (AudioContextCtor) {
      try {
        const context = new AudioContextCtor()
        baseLatencySeconds = context.baseLatency ?? 0
        outputLatencySeconds = 'outputLatency' in context
          ? Number((context as AudioContext & { outputLatency?: number }).outputLatency ?? 0)
          : 0
        await context.close()
      } catch (error) {
        debug('audio context latency read failed', error)
      }
    }

    const estimate = estimateRecordingLatencyMs({
      inputLatencySeconds,
      baseLatencySeconds,
      outputLatencySeconds,
    })
    setLatencyEstimateMs(estimate)
    return estimate
  }

  async function startRecording(trackKey: string) {
    if (recordingTrackKey) return
    setRecordingNotice('')
    const requestedTimelineStartMs = partialRecordingEnabled ? commitRecordingStartText() : 0
    if (requestedTimelineStartMs === null) return
    const takeTimelineStartMs = normalizeTimelineStartMs(requestedTimelineStartMs)

    const capability = recordingCapability()
    if (!capability.available) {
      setRecordingNotice(t(`recording.microphone.error.${capability.reason}`))
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      })
      const currentEstimateMs = await estimateCurrentLatency(stream)
      const takeOffsetMs = latencyMode === 'AUTO'
        ? currentEstimateMs
        : normalizeLatencyMs(manualLatencyMs)
      const mimeType = selectRecordingMimeType()
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)
      streamRef.current = stream
      recorderRef.current = recorder
      chunksRef.current = []

      recorder.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data)
      })

      recorder.addEventListener('stop', () => {
        const actualType = recorder.mimeType || mimeType || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type: actualType })
        chunksRef.current = []
        stream.getTracks().forEach((track) => track.stop())
        streamRef.current = null
        recorderRef.current = null
        setRecordingTrackKey(null)
        stopMonitorMix()

        if (blob.size <= 0) {
          setRecordingNotice(t('recording.microphone.error.EMPTY_RECORDING'))
          return
        }

        const objectUrl = URL.createObjectURL(blob)
        objectUrlsRef.current.add(objectUrl)
        const takeId = nextTakeId.current++
        setTakesByTrack((current) => {
          const old = current[trackKey] ?? []
          return {
            ...current,
            [trackKey]: [...old, {
              id: takeId,
              number: old.length + 1,
              objectUrl,
              mimeType: actualType,
              recordedAt: new Date().toISOString(),
              blob,
              offsetMs: takeOffsetMs,
              timelineStartMs: takeTimelineStartMs,
            }],
          }
        })
        setActiveTakeByTrack((current) => ({ ...current, [trackKey]: takeId }))
        setRecordingNotice(t('recording.microphone.savedLocally'))
        markDirty()
        debug('local take created', { trackKey, takeId, mimeType: actualType, size: blob.size, offsetMs: takeOffsetMs })
      })

      recorder.addEventListener('error', () => {
        stream.getTracks().forEach((track) => track.stop())
        streamRef.current = null
        recorderRef.current = null
        setRecordingTrackKey(null)
        stopMonitorMix()
        setRecordingNotice(t('recording.microphone.error.RECORDING_FAILED'))
      })

      const configuredCountInBeats = normalizeCountInBeats(countInBeats)
      if (configuredCountInBeats > 0) {
        setRecordingTrackKey(trackKey)
        const countInCompleted = await runCountIn()
        if (!countInCompleted) {
          stream.getTracks().forEach((track) => track.stop())
          if (streamRef.current === stream) streamRef.current = null
          if (recorderRef.current === recorder) recorderRef.current = null
          setRecordingTrackKey(null)
          return
        }
      }

      const window = partialRecordingEnabled
        ? preRollWindow(takeTimelineStartMs, preRollSeconds)
        : { playbackStartMs: 0, durationMs: 0 }
      const monitorStarted = await startMonitorMix(trackKey, window.playbackStartMs)

      if (partialRecordingEnabled && window.durationMs > 0 && monitorStarted) {
        setRecordingTrackKey(trackKey)
        const preRollCompleted = await waitForPreRoll(window.durationMs)
        if (!preRollCompleted) {
          stream.getTracks().forEach((track) => track.stop())
          if (streamRef.current === stream) streamRef.current = null
          if (recorderRef.current === recorder) recorderRef.current = null
          setRecordingTrackKey(null)
          return
        }
      }

      recorder.start(250)
      setRecordingTrackKey(trackKey)
      setCountInRemaining(0)
      setPreRollActive(false)
      debug('recording started', {
        trackKey,
        mimeType: recorder.mimeType,
        countInBeats: configuredCountInBeats,
        countInBpm,
        timelineStartMs: takeTimelineStartMs,
        preRollSeconds: partialRecordingEnabled ? preRollSeconds : 0,
      })
    } catch (error) {
      streamRef.current?.getTracks().forEach((track) => track.stop())
      streamRef.current = null
      recorderRef.current = null
      setRecordingTrackKey(null)
      stopMonitorMix()
      const name = error instanceof DOMException ? error.name : ''
      const key = name === 'NotAllowedError' || name === 'SecurityError'
        ? 'PERMISSION_DENIED'
        : name === 'NotFoundError'
          ? 'NO_MICROPHONE'
          : 'RECORDING_FAILED'
      setRecordingNotice(t(`recording.microphone.error.${key}`))
      debug('recording start failed', { name })
    }
  }

  function stopRecording() {
    if (countInRemaining > 0 && recorderRef.current?.state !== 'recording') {
      cancelCountIn()
      return
    }
    if (preRollActive && recorderRef.current?.state !== 'recording') {
      cancelPreRoll()
      return
    }
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
  }

  function selectActiveTake(trackKey: string, takeId: number) {
    stopMonitorMix()
    setActiveTakeByTrack((current) => ({ ...current, [trackKey]: takeId }))
    markDirty()
  }

  function deleteTake(trackKey: string, takeId: number) {
    if (!window.confirm(t('recording.take.deleteConfirm'))) return
    stopMonitorMix()
    const old = takesByTrack[trackKey] ?? []
    const removed = old.find((take) => take.id === takeId)
    if (removed) {
      URL.revokeObjectURL(removed.objectUrl)
      objectUrlsRef.current.delete(removed.objectUrl)
    }
    const remaining = old.filter((take) => take.id !== takeId)
    setTakesByTrack((current) => ({ ...current, [trackKey]: remaining }))
    setCompSegmentsByTrack((current) => ({
      ...current,
      [trackKey]: (current[trackKey] ?? []).filter((segment) => segment.takeId !== takeId),
    }))
    setActiveTakeByTrack((current) => {
      const next = { ...current }
      if (next[trackKey] === takeId) {
        if (remaining.length) next[trackKey] = remaining[remaining.length - 1].id
        else delete next[trackKey]
      }
      return next
    })
    markDirty()
  }

  function setTrackCompEnabled(trackKey: string, enabled: boolean) {
    stopMonitorMix()
    setCompEnabledByTrack((current) => ({ ...current, [trackKey]: enabled }))
    markDirty()
  }

  function addCompSegment(trackKey: string) {
    const takes = takesByTrack[trackKey] ?? []
    const selectedTakeId = activeTakeByTrack[trackKey] ?? takes[0]?.id
    if (!selectedTakeId) return
    const existing = sortedCompSegments(compSegmentsByTrack[trackKey])
    const startMs = existing.length ? existing[existing.length - 1].endMs : 0
    const segment: RecordingStudioCompSegment = {
      id: nextCompSegmentId.current++,
      takeId: selectedTakeId,
      startMs,
      endMs: startMs + 10_000,
    }
    setCompSegmentsByTrack((current) => ({
      ...current,
      [trackKey]: [...(current[trackKey] ?? []), segment],
    }))
    markDirty()
  }

  function updateCompSegment(trackKey: string, segmentId: number, patch: Partial<RecordingStudioCompSegment>) {
    stopMonitorMix()
    setCompSegmentsByTrack((current) => ({
      ...current,
      [trackKey]: (current[trackKey] ?? []).map((segment) => segment.id === segmentId
        ? {
            ...segment,
            ...patch,
            startMs: patch.startMs === undefined ? segment.startMs : normalizeCompTimeMs(patch.startMs),
            endMs: patch.endMs === undefined ? segment.endMs : normalizeCompTimeMs(patch.endMs),
          }
        : segment),
    }))
    markDirty()
  }

  function deleteCompSegment(trackKey: string, segmentId: number) {
    stopMonitorMix()
    setCompSegmentsByTrack((current) => ({
      ...current,
      [trackKey]: (current[trackKey] ?? []).filter((segment) => segment.id !== segmentId),
    }))
    markDirty()
  }

  function safeExportName(value: string, fallback: string): string {
    const cleaned = value.trim().replace(/[\\/:*?"<>|]+/g, '_')
    return cleaned || fallback
  }

  async function exportTake(trackName: string, take: LocalTake) {
    if (exporting || recordingTrackKey) return
    setExporting(true)
    setRecordingNotice(t('recording.export.preparingTake', { track: trackName, number: take.number }))
    try {
      const wav = await exportRecordingStudioMixToWav([{ blob: take.blob, volume: 1, offsetMs: 0 }])
      const url = URL.createObjectURL(wav)
      const link = document.createElement('a')
      const project = safeExportName(projectTitle, 'mioSegno-recording')
      const track = safeExportName(trackName, 'track')
      link.href = url
      link.download = `${project} - ${track} - Take ${take.number}.wav`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 1000)
      setRecordingNotice(t('recording.export.takeReady', { track: trackName, number: take.number }))
      debug('take export completed', { trackName, takeId: take.id, takeNumber: take.number, size: wav.size })
    } catch (error) {
      setRecordingNotice(t('recording.export.error'))
      debug('take export failed', { trackName, takeId: take.id, error })
    } finally {
      setExporting(false)
    }
  }

  async function exportMix() {
    if (exporting || recordingTrackKey) return
    const availableKeys = allTrackKeys.filter((key) => trackHasPlayback(key))
    const audibleKeys = audibleTrackKeys(availableKeys, trackMixByKey)
    if (audibleKeys.length === 0) {
      setRecordingNotice(t('recording.export.noTracks'))
      return
    }
    if (audibleKeys.some((key) => !validateTrackComp(key))) {
      setRecordingNotice(t('recording.comp.invalid'))
      return
    }

    setExporting(true)
    setRecordingNotice(t('recording.export.preparing'))
    try {
      const tracks: Array<{
        blob: Blob
        volume: number
        offsetMs: number
        timelineStartMs: number
        clipStartMs?: number
        clipEndMs?: number
      }> = []

      for (const key of audibleKeys) {
        const mix = getMix(key)
        const takes = takesByTrack[key] ?? []
        if (trackHasUsableComp(key)) {
          for (const segment of sortedCompSegments(compSegmentsByTrack[key])) {
            const take = takes.find((item) => item.id === segment.takeId)
            if (!take) continue
            tracks.push({
              blob: take.blob,
              volume: clampTrackVolume(mix.volume),
              offsetMs: normalizeLatencyMs(take.offsetMs ?? 0),
              timelineStartMs: normalizeTimelineStartMs(take.timelineStartMs ?? 0),
              clipStartMs: segment.startMs,
              clipEndMs: segment.endMs,
            })
          }
        } else {
          const take = activeTake(key)
          if (!take) continue
          tracks.push({
            blob: take.blob,
            volume: clampTrackVolume(mix.volume),
            offsetMs: normalizeLatencyMs(take.offsetMs ?? 0),
            timelineStartMs: normalizeTimelineStartMs(take.timelineStartMs ?? 0),
          })
        }
      }

      const wav = await exportRecordingStudioMixToWav(tracks)
      const url = URL.createObjectURL(wav)
      const link = document.createElement('a')
      link.href = url
      link.download = `${safeExportName(projectTitle, 'mioSegno-recording')}.wav`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 1000)
      setRecordingNotice(t('recording.export.ready'))
      debug('mix export completed', { tracks: tracks.length, size: wav.size })
    } catch (error) {
      setRecordingNotice(t('recording.export.error'))
      debug('mix export failed', error)
    } finally {
      setExporting(false)
    }
  }

  function renderMixControls(trackKey: string) {
    const mix = getMix(trackKey)
    return <div className="recording-mix-controls" aria-label={t('recording.mix.controls')}>
      <label className="recording-volume-control">
        <span>{t('recording.mix.volume')}</span>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={mix.volume}
          onChange={(event) => updateTrackMix(trackKey, { volume: Number(event.target.value) })}
        />
      </label>
<button
        className={mix.muted ? 'recording-mix-toggle active' : 'recording-mix-toggle'}
        type="button"
        aria-pressed={mix.muted}
        onClick={() => updateTrackMix(trackKey, { muted: !mix.muted })}
      >
        {t('recording.mix.mute')}
      </button>
      <button
        className={mix.solo ? 'recording-mix-toggle active' : 'recording-mix-toggle'}
        type="button"
        aria-pressed={mix.solo}
        onClick={() => updateTrackMix(trackKey, { solo: !mix.solo })}
      >
        {t('recording.mix.solo')}
      </button>
    </div>
  }

  function renderRecorder(trackKey: string, displayName: string) {
    const takes = takesByTrack[trackKey] ?? []
    const active = activeTakeByTrack[trackKey]
    const isRecording = recordingTrackKey === trackKey

    return <div className="recording-track-recorder">
      <div className="recording-track-toolbar">
        <div className="recording-track-actions">
          {isRecording
            ? <button className="button danger" type="button" onClick={stopRecording}>
                {countInRemaining > 0 ? t('recording.countIn.cancel') : preRollActive ? t('recording.partial.cancel') : t('recording.microphone.stop')}
              </button>
            : <button className="button secondary" type="button" disabled={recordingTrackKey !== null || !hydrated}
                onClick={() => void startRecording(trackKey)}>{t('recording.microphone.start')}</button>}
          {isRecording && <span className={countInRemaining > 0 || preRollActive ? 'recording-countin-indicator' : 'recording-live-indicator'}>
            {countInRemaining > 0
              ? t('recording.countIn.remaining', { count: countInRemaining })
              : preRollActive
                ? t('recording.partial.preparing')
                : t('recording.microphone.recording')}
          </span>}
        </div>
        {renderMixControls(trackKey)}
      </div>

      {takes.length > 1 && <details className="recording-comp-panel recording-comp-header-toggle">
        <summary className="recording-comp-summary">{t('recording.comp.title')}</summary>
        <div className="recording-comp-toolbar">
          <label className="recording-comp-toggle">
            <input
              type="checkbox"
              checked={Boolean(compEnabledByTrack[trackKey])}
              disabled={isRecording || exporting}
              onChange={(event) => setTrackCompEnabled(trackKey, event.target.checked)}
            />
            <span>{t('recording.comp.enable')}</span>
          </label>
          <button className="button secondary compact" type="button"
            disabled={isRecording || exporting || !compEnabledByTrack[trackKey]}
            onClick={() => addCompSegment(trackKey)}>
            {t('recording.comp.addSegment')}
          </button>
        </div>
        <p className="recording-comp-hint">{t('recording.comp.description')}</p>
        {compEnabledByTrack[trackKey] && !validateTrackComp(trackKey)
          && <p className="notice warning">{t('recording.comp.invalid')}</p>}
        {compEnabledByTrack[trackKey] && <div className="recording-comp-segments">
          {(compSegmentsByTrack[trackKey] ?? []).map((segment, index) => <div className="recording-comp-segment" key={segment.id}>
            <strong>{t('recording.comp.segment', { number: index + 1 })}</strong>
            <label className="recording-comp-field">
              <span>{t('recording.comp.startSeconds')}</span>
              <input type="number" min="0" step="0.1" value={segment.startMs / 1000}
                disabled={isRecording || exporting}
                onChange={(event) => updateCompSegment(trackKey, segment.id, { startMs: Number(event.target.value) * 1000 })} />
            </label>
            <label className="recording-comp-field">
              <span>{t('recording.comp.endSeconds')}</span>
              <input type="number" min="0" step="0.1" value={segment.endMs / 1000}
                disabled={isRecording || exporting}
                onChange={(event) => updateCompSegment(trackKey, segment.id, { endMs: Number(event.target.value) * 1000 })} />
            </label>
            <label className="recording-comp-field recording-comp-take-field">
              <span>{t('recording.comp.take')}</span>
              <select value={segment.takeId} disabled={isRecording || exporting}
                onChange={(event) => updateCompSegment(trackKey, segment.id, { takeId: Number(event.target.value) })}>
                {takes.map((take) => <option value={take.id} key={take.id}>{t('recording.take.label', { number: take.number })}</option>)}
              </select>
            </label>
            <button className="button danger compact" type="button" disabled={isRecording || exporting}
              onClick={() => deleteCompSegment(trackKey, segment.id)}>
              {t('recording.comp.deleteSegment')}
            </button>
          </div>)}
          {(compSegmentsByTrack[trackKey] ?? []).length === 0 && <p className="recording-take-empty">{t('recording.comp.empty')}</p>}
        </div>}
      </details>}

      {takes.length === 0
        ? <p className="recording-take-empty">{t('recording.take.empty')}</p>
        : <div className="recording-take-list">{takes.map((take) =>
          <article className={take.id === active ? 'recording-take-card active recording-take-card-inline' : 'recording-take-card recording-take-card-inline'} key={take.id}>
            <div className="recording-take-heading recording-take-heading-inline">
              <button className="recording-take-select recording-take-select-inline" type="button" aria-pressed={take.id === active}
                onClick={() => selectActiveTake(trackKey, take.id)}>
                <strong>{t('recording.take.label', { number: take.number })}</strong>
                <span>{take.id === active ? t('recording.take.active') : t('recording.take.makeActive')}</span>
                {take.timelineStartMs > 0 && <span>{t('recording.partial.takeStart', { time: formatTimelinePosition(take.timelineStartMs) })}</span>}
              </button>
              <audio className="recording-take-audio-inline" controls preload="metadata" src={take.objectUrl} />
              <div className="recording-take-actions">
                <label className="recording-take-offset">
                  <span>{t('recording.latency.takeOffset')}</span>
                  <input
                    type="number"
                    min="0"
                    max="500"
                    step="1"
                    value={take.offsetMs}
                    disabled={isRecording}
                    onChange={(event) => updateTakeOffset(trackKey, take.id, Number(event.target.value))}
                  />
                  <span>{t('recording.latency.ms')}</span>
                </label>
                <button
                  className="button secondary compact"
                  type="button"
                  disabled={isRecording || exporting}
                  onClick={() => void exportTake(displayName, take)}
                >
                  {t('recording.export.takeWav')}
                </button>
                <button
                  className="button danger compact"
                  type="button"
                  disabled={isRecording}
                  onClick={() => deleteTake(trackKey, take.id)}
                >
                  {t('recording.take.delete')}
                </button>
              </div>
            </div>
          </article>
        )}</div>}
    </div>
  }

  const accompanimentOptions = [
    ['NONE', t('recording.accompaniment.none'), t('recording.accompaniment.noneDescription')],
    ['UPLOAD', t('recording.accompaniment.upload'), t('recording.accompaniment.uploadDescription')],
    ['RECORD', t('recording.accompaniment.record'), t('recording.accompaniment.recordDescription')],
  ] as const

  return <section className="recording-studio-page" aria-labelledby="recording-studio-title">
    <header className="card recording-studio-hero">
      <div className="recording-studio-hero-row">
        <div>
          <p className="recording-studio-eyebrow">{t('recording.eyebrow')}</p>
          <h1 id="recording-studio-title">{t('recording.title')}</h1>
          <p>{t('recording.description')}</p>
        </div>
        <div className="recording-master-actions">
          <button
            className="button secondary"
            type="button"
            disabled={recordingTrackKey !== null || allTrackKeys.every((key) => !trackHasPlayback(key))}
            onClick={() => void startMonitorMix(undefined, playheadMsRef.current)}
          >
            {t('recording.mix.playAll')}
          </button>
          <button className="button secondary" type="button" disabled={!mixPlaying} onClick={stopMonitorMix}>
            {t('recording.mix.stopAll')}
          </button>
          <button
            className="button primary"
            type="button"
            disabled={recordingTrackKey !== null || exporting || allTrackKeys.every((key) => !trackHasPlayback(key))}
            onClick={() => void exportMix()}
          >
            {exporting ? t('recording.export.exporting') : t('recording.export.wav')}
          </button>
        </div>
        <div className="recording-master-transport">
          <div className="recording-master-transport-row">
            <strong>{formatTimelinePosition(playheadMs)} / {formatTimelinePosition(projectDurationMs)}</strong>
            <button
              className="button secondary compact"
              type="button"
              disabled={recordingTrackKey !== null || projectDurationMs <= 0}
              onClick={goMasterPlayheadToStart}
            >
              {t('recording.transport.toStart')}
            </button>
          </div>
          <input
            className="recording-master-playhead"
            type="range"
            min="0"
            max={Math.max(1, projectDurationMs)}
            step="100"
            value={Math.min(playheadMs, Math.max(1, projectDurationMs))}
            disabled={recordingTrackKey !== null || projectDurationMs <= 0}
            aria-label={t('recording.transport.seek')}
            onChange={(event) => seekMasterPlayhead(Number(event.target.value))}
          />
        </div>
        <div className="recording-latency-panel">
          <div className="recording-latency-heading">
            <strong>{t('recording.latency.title')}</strong>
            <span>{t('recording.latency.description')}</span>
          </div>
          <div className="recording-latency-controls">
            <label className="recording-latency-mode">
              <input
                type="radio"
                name="recording-latency-mode"
                value="AUTO"
                checked={latencyMode === 'AUTO'}
                onChange={() => { setLatencyMode('AUTO'); markDirty() }}
              />
              <span>{t('recording.latency.auto')}</span>
            </label>
            <label className="recording-latency-mode">
              <input
                type="radio"
                name="recording-latency-mode"
                value="MANUAL"
                checked={latencyMode === 'MANUAL'}
                onChange={() => { setLatencyMode('MANUAL'); markDirty() }}
              />
              <span>{t('recording.latency.manual')}</span>
            </label>
            <label className="recording-latency-number">
              <span>{t('recording.latency.compensation')}</span>
              <input
                type="number"
                min="0"
                max="500"
                step="1"
                value={manualLatencyMs}
                disabled={latencyMode !== 'MANUAL'}
                onChange={(event) => { setManualLatencyMs(normalizeLatencyMs(Number(event.target.value))); markDirty() }}
              />
              <span>{t('recording.latency.ms')}</span>
            </label>
            <span className="recording-latency-estimate">
              {t('recording.latency.estimate', { ms: latencyEstimateMs })}
            </span>
          </div>
        </div>
        <div className="recording-countin-panel">
          <div className="recording-countin-heading">
            <strong>{t('recording.countIn.title')}</strong>
            <span>{t('recording.countIn.description')}</span>
          </div>
          <div className="recording-countin-controls">
            <label>
              <span>{t('recording.countIn.beats')}</span>
              <select
                value={countInBeats}
                disabled={recordingTrackKey !== null || projectBusy || exporting}
                onChange={(event) => {
                  setCountInBeats(normalizeCountInBeats(event.target.value))
                  markDirty()
                }}
              >
                <option value={0}>{t('recording.countIn.none')}</option>
                <option value={2}>{t('recording.countIn.beatOption', { count: 2 })}</option>
                <option value={4}>{t('recording.countIn.beatOption', { count: 4 })}</option>
                <option value={8}>{t('recording.countIn.beatOption', { count: 8 })}</option>
              </select>
            </label>
            <label>
              <span>{t('recording.countIn.bpm')}</span>
              <input
                type="number"
                min="40"
                max="240"
                step="1"
                value={countInBpm}
                disabled={recordingTrackKey !== null || projectBusy || exporting}
                onChange={(event) => {
                  setCountInBpm(normalizeCountInBpm(event.target.value))
                  markDirty()
                }}
              />
            </label>
          </div>
        </div>
        <div className="recording-partial-panel">
          <div className="recording-partial-summary">
            <span>{t('recording.partial.title')}</span>
            <label className="recording-partial-toggle">
              <input
                type="checkbox"
                checked={partialRecordingEnabled}
                disabled={recordingTrackKey !== null || projectBusy || exporting}
                onChange={(event) => {
                  setPartialRecordingEnabled(event.target.checked)
                  markDirty()
                }}
              />
              <span>{t('recording.partial.enable')}</span>
            </label>
          </div>
          {partialRecordingEnabled && <>
            <p>{t('recording.partial.description')}</p>
            <div className="recording-partial-controls">
            <label>
              <span>{t('recording.partial.startPosition')}</span>
              <input
                type="text"
                inputMode="numeric"
                value={recordingStartText}
                disabled={recordingTrackKey !== null || projectBusy || exporting}
                placeholder="01:20"
                onChange={(event) => setRecordingStartText(event.target.value)}
                onBlur={() => {
                  const parsed = commitRecordingStartText()
                  if (parsed !== null) markDirty()
                }}
              />
            </label>
            <label>
              <span>{t('recording.partial.preRoll')}</span>
              <select
                value={preRollSeconds}
                disabled={recordingTrackKey !== null || projectBusy || exporting}
                onChange={(event) => {
                  setPreRollSeconds(normalizePreRollSeconds(event.target.value))
                  markDirty()
                }}
              >
                <option value={0}>{t('recording.partial.none')}</option>
                <option value={2}>{t('recording.partial.seconds', { count: 2 })}</option>
                <option value={4}>{t('recording.partial.seconds', { count: 4 })}</option>
                <option value={8}>{t('recording.partial.seconds', { count: 8 })}</option>
              </select>
            </label>
          </div>
          </>}
        </div>
      </div>
      <div className="recording-project-manager">
        <label className="recording-project-picker">
          <span>{t('recording.projects.label')}</span>
          <select value={currentProjectId ?? ''} disabled={!hydrated || projectBusy || recordingTrackKey !== null || exporting} onChange={(event) => void openProject(event.target.value)}>
            {projects.map((project) => <option key={project.id} value={project.id}>{project.title.trim() || t('recording.projects.untitled')}</option>)}
          </select>
        </label>
        <div className="recording-project-actions">
          <button className="button secondary compact" type="button"
            disabled={!hydrated || projectBusy || recordingTrackKey !== null || exporting}
            onClick={() => void newProject()}>
            {t('recording.projects.new')}
          </button>
          <button className="button secondary compact" type="button"
            disabled={!hydrated || !currentProjectId || projectBusy || recordingTrackKey !== null || exporting}
            onClick={() => void renameCurrentProject()}>
            {t('recording.projects.renameAction')}
          </button>
          <button className="button secondary compact" type="button"
            disabled={!hydrated || !currentProjectId || projectBusy || recordingTrackKey !== null || exporting}
            onClick={() => void duplicateProject()}>
            {t('recording.projects.duplicate')}
          </button>
          <button className="button secondary compact" type="button"
            disabled={!hydrated || !currentProjectId || projectBusy || recordingTrackKey !== null || exporting}
            onClick={() => void exportCurrentProjectFile()}>
            {t('recording.projects.exportFile')}
          </button>
          <button className="button secondary compact" type="button"
            disabled={!hydrated || projectBusy || recordingTrackKey !== null || exporting}
            onClick={() => projectImportInputRef.current?.click()}>
            {t('recording.projects.importFile')}
          </button>
          <input
            ref={projectImportInputRef}
            className="recording-project-file-input"
            type="file"
            accept=".miosegno-studio,application/vnd.miosegno.studio"
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) void importProjectFile(file)
            }}
          />
          <button className="button danger compact" type="button"
            disabled={!hydrated || !currentProjectId || projectBusy || recordingTrackKey !== null || exporting}
            onClick={() => void deleteCurrentProject()}>
            {t('recording.projects.delete')}
          </button>
        </div>
      </div>
      <p className="notice warning">{t('recording.mix.headphones')}</p>
      <p className="notice">{t('recording.persistence.notice')}</p>
    </header>

    {recordingNotice && <p className="notice" role="status">{recordingNotice}</p>}

    <section className="card recording-studio-section" aria-labelledby="recording-accompaniment-heading">
      <div className="recording-studio-section-heading"><div>
        <h2 id="recording-accompaniment-heading">{t('recording.accompaniment.title')}</h2>
        <p>{t('recording.accompaniment.description')}</p>
      </div></div>
      <div className="recording-studio-source-grid" role="group" aria-label={t('recording.accompaniment.title')}>
        {accompanimentOptions.map(([source, title, description]) =>
          <button key={source}
            className={source === accompanimentSource ? 'recording-studio-source-option selected' : 'recording-studio-source-option'}
            type="button" aria-pressed={source === accompanimentSource} disabled={!hydrated}
            onClick={() => changeAccompanimentSource(source)}>
            <strong>{title}</strong><span>{description}</span>
          </button>
        )}
      </div>
      {accompanimentSource === 'UPLOAD' && <div className="recording-accompaniment-upload-panel">
        <div className="recording-accompaniment-upload-row">
          <button className="button secondary compact" type="button"
            disabled={accompanimentUploadBusy || recordingTrackKey !== null || exporting || projectBusy}
            onClick={() => accompanimentUploadInputRef.current?.click()}>
            {activeTake(UPLOADED_ACCOMPANIMENT_TRACK)
              ? t('recording.accompaniment.replaceFile')
              : t('recording.accompaniment.chooseFile')}
          </button>
          <input
            ref={accompanimentUploadInputRef}
            className="recording-project-file-input"
            type="file"
            accept=".mp3,.m4a,.aac,.flac,.ogg,.wav,.webm,audio/*"
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) void chooseUploadedAccompaniment(file)
            }}
          />
          <strong className="recording-accompaniment-upload-name">
            {uploadedAccompanimentName || t('recording.accompaniment.noFile')}
          </strong>
          {accompanimentUploadBusy && <span>{t('recording.accompaniment.checkingFile')}</span>}
        </div>
        {activeTake(UPLOADED_ACCOMPANIMENT_TRACK) && <div className="recording-accompaniment-upload-track">
          <audio controls preload="metadata" src={activeTake(UPLOADED_ACCOMPANIMENT_TRACK)?.objectUrl} />
          <label className="recording-volume-control">
            <span>{t('recording.mix.volume')}</span>
            <input type="range" min="0" max="1" step="0.05"
              value={getMix(UPLOADED_ACCOMPANIMENT_TRACK).volume}
              onChange={(event) => {
                const volume = clampTrackVolume(Number(event.target.value))
                setTrackMixByKey((current) => ({
                  ...current,
                  [UPLOADED_ACCOMPANIMENT_TRACK]: { ...getMix(UPLOADED_ACCOMPANIMENT_TRACK), volume },
                }))
                markDirty()
              }} />
          </label>
          <button className={getMix(UPLOADED_ACCOMPANIMENT_TRACK).muted ? 'button secondary compact active' : 'button secondary compact'}
            type="button"
            onClick={() => {
              const mix = getMix(UPLOADED_ACCOMPANIMENT_TRACK)
              setTrackMixByKey((current) => ({ ...current, [UPLOADED_ACCOMPANIMENT_TRACK]: { ...mix, muted: !mix.muted } }))
              markDirty()
            }}>
            {t('recording.mix.mute')}
          </button>
          <button className={getMix(UPLOADED_ACCOMPANIMENT_TRACK).solo ? 'button secondary compact active' : 'button secondary compact'}
            type="button"
            onClick={() => {
              const mix = getMix(UPLOADED_ACCOMPANIMENT_TRACK)
              setTrackMixByKey((current) => ({ ...current, [UPLOADED_ACCOMPANIMENT_TRACK]: { ...mix, solo: !mix.solo } }))
              markDirty()
            }}>
            {t('recording.mix.solo')}
          </button>
        </div>}
      </div>}
      {accompanimentSource === 'RECORD' && renderRecorder('accompaniment', t('recording.accompaniment.title'))}
    </section>

    <section className="card recording-studio-section" aria-labelledby="recording-parts-heading">
      <div className="recording-studio-section-heading recording-studio-parts-heading">
        <div>
          <h2 id="recording-parts-heading">{t('recording.parts.title')}</h2>
          <p>{t('recording.parts.description')}</p>
        </div>
        <button className="button primary" type="button" disabled={!hydrated} onClick={addPart}>
          {t('recording.parts.add')}
        </button>
      </div>

      {parts.length === 0
        ? <p className="recording-studio-empty">{t('recording.parts.empty')}</p>
        : <div className="recording-studio-part-list">{parts.map((part, index) => {
          const key = `part:${part.id}`
          return <article className="recording-studio-part-card recording-studio-part-card-expanded" key={part.id}>
            <div className="recording-studio-part-main recording-studio-part-main-inline">
              <div className="recording-studio-part-index" aria-hidden="true">{index + 1}</div>
              <label className="recording-studio-field recording-studio-part-name">
                <span>{t('recording.part.nameLabel')}</span>
                <input type="text" value={part.name} maxLength={120}
                  placeholder={t('recording.part.namePlaceholder')}
                  onChange={(event) => renamePart(part.id, event.target.value)} />
              </label>
              <button className="button danger compact" type="button" disabled={recordingTrackKey === key}
                onClick={() => deletePart(part.id)}>{t('recording.part.delete')}</button>
            </div>
            {renderRecorder(key, part.name.trim() || t('recording.part.defaultName', { number: index + 1 }))}
          </article>
        })}</div>}
    </section>
  </section>
}
